import React from "react";
import classNames from "classnames";
import get from "lodash/get";
import {
  RegisterOptions,
  DeepMap,
  FieldError,
  UseFormRegister,
  Path,
  FieldValues,
} from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { Input, InputProps } from "./Input";
import { FormErrorMessage } from "./formErrorMessage";

export type FormInputProps<TFormValues extends FieldValues> = {
  name: Path<TFormValues>;
  disabled?: any;
  rules?: RegisterOptions;
  register?: UseFormRegister<TFormValues>;
  errors?: Partial<DeepMap<TFormValues, FieldError>>;
} & Omit<InputProps, "name">;

export const FormInput = <TFormValues extends Record<string, unknown>>({
  name,
  disabled,
  register,
  rules,
  errors,
  className,
  ...props
}: FormInputProps<TFormValues>): JSX.Element => {
  // If the name is in a FieldArray, it will be 'fields.index.fieldName' and errors[name] won't return anything, so we are using lodash get
  const errorMessages = get(errors, name);
  const hasError = !!(errors && errorMessages);
  const inRef: any = React.useRef<HTMLInputElement>(null);
  return (
    <div className={classNames("", className)} aria-live="polite">
      <Input
        ref={inRef}
        name={name}
        aria-invalid={hasError}
        disabled={disabled}
        className={classNames({
          "Input-form-disabled": disabled,
          "input-form-error": hasError,
        })}
        {...props}
        {...(register &&
          register(
            name,
            rules as RegisterOptions<TFormValues, Path<TFormValues>>
          ))}
      />
      <ErrorMessage
        errors={errors}
        name={name as any}
        render={({ message }: any) => (
          <FormErrorMessage className="mt-1 input-form-error-message">
            {message}
          </FormErrorMessage>
        )}
      />
    </div>
  );
};
